import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
} from '@ludus/ui'
import type { InternalStatus, PipelineId, StatusId } from '@ludus/core'
import { deactivateStatus, PipelineApiError } from '@/lib/api/pipelines.ts'
import { apiErrorMessage } from '@/shared/feedback/apiErrorMessage.ts'

interface RetireStageDialogProps {
  open: boolean
  onOpenChange: (next: boolean) => void
  pipelineId: PipelineId
  stage: InternalStatus
  /** The other active stages — the only places leads can be moved to. */
  siblings: InternalStatus[]
  onRetired: (stageId: StatusId) => Promise<unknown>
}

/**
 * Retiring is attempted before the leads are counted: the engine refuses a stage
 * that still holds them and answers with the count, which is the only place that
 * number is authoritative. So the dialog asks for a destination when it is told
 * to, rather than pre-fetching a count that could be stale by the time Retire is
 * pressed.
 */
export function RetireStageDialog({
  open,
  onOpenChange,
  pipelineId,
  stage,
  siblings,
  onRetired,
}: RetireStageDialogProps) {
  const { t } = useTranslation()
  // Mounted only while a stage is being retired, so first-render state is the
  // reset — no effect has to re-arm it, and a parent re-render can't drop the
  // count the engine just reported.
  const [leadCount, setLeadCount] = useState<number | null>(null)
  const [migrateTo, setMigrateTo] = useState<StatusId | null>(siblings[0]?.id ?? null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<PipelineApiError | null>(null)
  const needsDestination = leadCount !== null

  const retire = async () => {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const result = await deactivateStatus(pipelineId, stage.id, {
        migrateTo: needsDestination ? (migrateTo ?? undefined) : undefined,
      })
      if (result.deactivated) {
        await onRetired(stage.id)
        onOpenChange(false)
        return
      }
      setLeadCount(result.leadCount)
    } catch (err) {
      setError(
        err instanceof PipelineApiError
          ? err
          : new PipelineApiError({ kind: 'unknown', status: null, message: 'retire failed' }),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="min-w-0">
            <DialogTitle>
              {t('pages.pipelines.settings.retire.title', {
                name: stage.name,
                defaultValue: 'Retire {{name}}?',
              })}
            </DialogTitle>
            <DialogDescription>
              {needsDestination
                ? t('pages.pipelines.settings.retire.migrateBody', {
                    count: leadCount ?? 0,
                    name: stage.name,
                    defaultValue:
                      '{{count}} lead(s) still sit in {{name}}. Pick where they go — they move before the stage is retired.',
                  })
                : t('pages.pipelines.settings.retire.body', {
                    defaultValue:
                      'The stage leaves the board and its settings. Leads that already passed through it keep their history.',
                  })}
            </DialogDescription>
          </div>
          <DialogClose />
        </DialogHeader>
        <DialogBody className="py-5">
          {needsDestination && migrateTo !== null && (
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">
                {t('pages.pipelines.settings.retire.destination', {
                  defaultValue: 'Move leads to',
                })}
              </span>
              <Select<StatusId>
                value={migrateTo}
                onValueChange={setMigrateTo}
                options={siblings.map((s) => ({ value: s.id, label: s.name }))}
                className="w-full"
                triggerClassName="w-full"
              />
            </label>
          )}
          {error && (
            <p role="alert" className="mt-4 text-[11px] font-medium text-toast-error-text">
              {apiErrorMessage(error, t, 'pages.pipelines.settings.retire')}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            {t('pages.pipelines.settings.retire.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={retire}
            disabled={busy || (needsDestination && migrateTo === null)}
          >
            {needsDestination
              ? t('pages.pipelines.settings.retire.confirmMigrate', {
                  defaultValue: 'Move and retire',
                })
              : t('pages.pipelines.settings.retire.confirm', { defaultValue: 'Retire' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
